const MAIN_BUNDLE_PATTERN = /\/static\/js\/main\.[^/]+\.js$/;
const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

function getPublicUrl() {
  const publicUrl = process.env.PUBLIC_URL || "";
  return publicUrl.endsWith("/") ? publicUrl.slice(0, -1) : publicUrl;
}

function normalizeAssetPath(assetPath) {
  if (!assetPath) return "";

  try {
    return new URL(assetPath, window.location.origin).pathname;
  } catch (_error) {
    return assetPath.startsWith("/") ? assetPath : `/${assetPath}`;
  }
}

function getCurrentMainBundlePath() {
  const scripts = Array.from(document.scripts || []);
  const mainScript = scripts
    .map((script) => script.getAttribute("src"))
    .filter(Boolean)
    .map(normalizeAssetPath)
    .find((src) => MAIN_BUNDLE_PATTERN.test(src));

  return mainScript || "";
}

function getLatestMainBundlePath(manifest) {
  const manifestFile = manifest && manifest.files && manifest.files["main.js"];
  if (manifestFile) return normalizeAssetPath(manifestFile);

  const entrypoint = manifest && Array.isArray(manifest.entrypoints)
    ? manifest.entrypoints.find((entry) => MAIN_BUNDLE_PATTERN.test(normalizeAssetPath(entry)))
    : "";

  return normalizeAssetPath(entrypoint);
}

function getBundleVersion(bundlePath) {
  const fileName = bundlePath.split("/").pop() || "";
  return fileName.replace(/^main\./, "").replace(/\.js$/, "") || `${Date.now()}`;
}

function buildVersionedUrl(latestMainBundlePath) {
  const nextUrl = new URL(window.location.href);
  nextUrl.searchParams.set("appVersion", getBundleVersion(latestMainBundlePath));
  return nextUrl.toString();
}

async function fetchLatestManifest() {
  const manifestUrl = new URL(`${getPublicUrl()}/asset-manifest.json`, window.location.origin);
  manifestUrl.searchParams.set("cacheBust", `${Date.now()}`);

  const response = await fetch(manifestUrl.toString(), {
    cache: "no-store",
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

  if (!response.ok) return null;
  return response.json();
}

export function startAppVersionGuard(options = {}) {
  if (process.env.NODE_ENV !== "production") return () => {};
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};

  const intervalMs = options.intervalMs || DEFAULT_CHECK_INTERVAL_MS;
  let checkInProgress = false;
  let disposed = false;

  const checkForNewVersion = async () => {
    if (checkInProgress || disposed) return;

    const currentMainBundlePath = getCurrentMainBundlePath();
    if (!currentMainBundlePath) return;

    checkInProgress = true;

    try {
      const manifest = await fetchLatestManifest();
      const latestMainBundlePath = getLatestMainBundlePath(manifest);

      if (!latestMainBundlePath || latestMainBundlePath === currentMainBundlePath) return;

      const reloadKey = `cap-app-version-reloaded:${latestMainBundlePath}`;
      if (window.sessionStorage.getItem(reloadKey) === "1") return;

      window.sessionStorage.setItem(reloadKey, "1");
      window.location.replace(buildVersionedUrl(latestMainBundlePath));
    } catch (_error) {
      // Version refresh is best-effort; failed checks should not interrupt users.
    } finally {
      checkInProgress = false;
    }
  };

  const startupTimer = window.setTimeout(checkForNewVersion, 3000);
  const intervalTimer = window.setInterval(checkForNewVersion, intervalMs);
  const onFocus = () => checkForNewVersion();
  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") checkForNewVersion();
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    disposed = true;
    window.clearTimeout(startupTimer);
    window.clearInterval(intervalTimer);
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
